/**
 * BootLoadingScreen status copy — timer baseline + opportunistic signal upgrades.
 * Presentational only. Never gates auth, routing, or dismiss of the boot surface.
 */

import { diagnosticLog } from '../utils/diagnosticLogger';

export const BOOT_SURFACE_FORCE_DISMISS_MS = 30_000;

export type BootSurfaceTimerPhase = {
  atMs: number;
  id: string;
  message: string;
};

/** Time-driven baseline — always advances; guarantees non-blank dead space. */
export const BOOT_SURFACE_TIMER_PHASES: BootSurfaceTimerPhase[] = [
  { atMs: 0, id: 'starting', message: 'Starting Maestro…' },
  { atMs: 5_000, id: 'loading_library_timer', message: 'Loading your library…' },
  {
    atMs: 12_000,
    id: 'almost_there',
    message: 'Almost there — large libraries take a little longer',
  },
];

type Listener = () => void;

let albumCount = 0;
let daemonReady = false;
let listeners = new Set<Listener>();
let bootOriginMs = typeof performance !== 'undefined' ? performance.now() : 0;
const emittedPhases = new Set<string>();

function emit(): void {
  listeners.forEach((fn) => {
    try { fn(); } catch { /* ignore */ }
  });
}

export function resetBootSurfaceStatusForTests(): void {
  albumCount = 0;
  daemonReady = false;
  listeners = new Set();
  bootOriginMs = typeof performance !== 'undefined' ? performance.now() : 0;
  emittedPhases.clear();
}

export function subscribeBootSurfaceStatus(listener: Listener): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function noteBootSurfaceAlbumCount(count: number): void {
  const n = Math.max(0, Math.floor(Number(count) || 0));
  if (n === albumCount) return;
  albumCount = n;
  emit();
}

export function noteBootSurfaceDaemonReady(ready = true): void {
  if (daemonReady === !!ready) return;
  daemonReady = !!ready;
  emit();
}

export function getBootSurfaceSignalMessage(): string | null {
  if (albumCount > 0) {
    return `Loading albums — ${albumCount.toLocaleString()} so far`;
  }
  if (daemonReady) {
    return 'Loading your library…';
  }
  return null;
}

export function timerPhaseAt(elapsedMs: number): BootSurfaceTimerPhase {
  let current = BOOT_SURFACE_TIMER_PHASES[0];
  for (const phase of BOOT_SURFACE_TIMER_PHASES) {
    if (elapsedMs >= phase.atMs) current = phase;
  }
  return current;
}

export type BootSurfaceResolved = {
  phase: string;
  message: string;
  source: 'timer' | 'signal';
  albumCount: number;
};

/** Prefer live signals when present; otherwise the timer baseline. */
export function resolveBootSurfaceStatus(elapsedMs: number): BootSurfaceResolved {
  const signal = getBootSurfaceSignalMessage();
  if (signal) {
    return {
      phase: albumCount > 0 ? 'loading_albums' : 'loading_library',
      message: signal,
      source: 'signal',
      albumCount,
    };
  }
  const timer = timerPhaseAt(elapsedMs);
  return {
    phase: timer.id,
    message: timer.message,
    source: 'timer',
    albumCount,
  };
}

export function emitBootSurfacePhaseBeacon(
  phase: string,
  extra: Record<string, unknown> = {},
): void {
  if (!phase || emittedPhases.has(phase)) return;
  emittedPhases.add(phase);
  const elapsedMs = typeof performance !== 'undefined'
    ? Math.round((performance.now() - bootOriginMs) * 10) / 10
    : 0;
  diagnosticLog('info', 'js:render', 'boot_progress', {
    kind: 'boot_progress',
    phase,
    elapsed_ms: elapsedMs,
    ...extra,
  });
}

export function emitBootSurfaceForcedDismissBeacon(elapsedMs: number): void {
  diagnosticLog('warn', 'js:render', 'boot_surface_forced_dismiss', {
    kind: 'boot_surface_forced_dismiss',
    elapsed_ms: Math.round(elapsedMs),
  });
}
